import { useEffect, useId, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import {
  ALL_GRANTED,
  ONLY_NECESSARY,
  onOpenConsentSettings,
  readConsent,
  saveConsent,
  type ConsentChoice,
} from "@/lib/consent";
import { cn } from "@/lib/utils";

const CATEGORIES: { key: keyof ConsentChoice; label: string; text: string }[] = [
  {
    key: "functional",
    label: "Funktionella",
    text: "Gör att inbäddade tjänster och extra funktioner fungerar och kommer ihåg dina val.",
  },
  {
    key: "analytics",
    label: "Statistik",
    text: "Hjälper oss förstå hur sajten används, så att vi kan förbättra den. Uppgifterna är sammanställda.",
  },
  {
    key: "marketing",
    label: "Marknadsföring",
    text: "Används för att mäta och anpassa annonser hos t.ex. Google och Meta.",
  },
];

/**
 * Egen cookiebanner (ersätter Cookiebot). Renderas i serverns HTML så att den
 * syns redan vid första målningen; för den som redan valt döljs den av CSS
 * (`html[data-consent="set"]`) innan React har laddats.
 */
const ConsentBanner = () => {
  const titleId = useId();
  const [visible, setVisible] = useState(true);
  const [forcedOpen, setForcedOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice>(ONLY_NECESSARY);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      setChoice(stored);
      setVisible(false);
    }
    return onOpenConsentSettings(() => {
      const current = readConsent();
      if (current) setChoice(current);
      setForcedOpen(true);
      setShowSettings(true);
      setVisible(true);
      requestAnimationFrame(() => panelRef.current?.focus());
    });
  }, []);

  if (!visible) return null;

  const decide = (next: ConsentChoice) => {
    saveConsent(next);
    setChoice(next);
    setVisible(false);
    setForcedOpen(false);
    setShowSettings(false);
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      data-consent-banner=""
      data-open={forcedOpen ? "" : undefined}
      className="fixed inset-x-0 bottom-0 z-[60] p-3 outline-none sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md sm:p-0"
    >
      <div className="rounded-2xl border bg-card p-4 text-sm shadow-xl sm:p-5">
        <h2 id={titleId} className="text-base font-semibold text-foreground">
          Vi använder cookies
        </h2>
        <p className="mt-1.5 text-foreground/80">
          Nödvändiga cookies behövs för att sajten ska fungera. Med ditt samtycke använder vi även cookies för
          statistik och marknadsföring. Läs mer i vår{" "}
          <Link to="/cookies" className="font-medium text-action-800 underline underline-offset-2">
            cookiepolicy
          </Link>
          .
        </p>

        {showSettings && (
          <fieldset className="mt-4 space-y-3">
            <legend className="sr-only">Välj vilka cookies du godkänner</legend>
            <div className="flex items-start gap-3">
              <input type="checkbox" checked disabled className="mt-1 size-4 accent-[var(--color-action-800)]" id={`${titleId}-nec`} />
              <label htmlFor={`${titleId}-nec`} className="text-foreground/80">
                <span className="font-medium text-foreground">Nödvändiga</span> – krävs för inloggning, säkerhet och
                för att spara ditt cookieval. Alltid på.
              </label>
            </div>
            {CATEGORIES.map((c) => (
              <div key={c.key} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id={`${titleId}-${c.key}`}
                  checked={choice[c.key]}
                  onChange={(e) => setChoice((prev) => ({ ...prev, [c.key]: e.target.checked }))}
                  className="mt-1 size-4 cursor-pointer accent-[var(--color-action-800)]"
                />
                <label htmlFor={`${titleId}-${c.key}`} className="cursor-pointer text-foreground/80">
                  <span className="font-medium text-foreground">{c.label}</span> – {c.text}
                </label>
              </div>
            ))}
          </fieldset>
        )}

        <div className="mt-4 grid grid-cols-2 gap-2">
          {showSettings ? (
            <>
              <button
                type="button"
                onClick={() => decide(choice)}
                className="col-span-2 min-h-11 rounded-full border border-action-800 px-4 font-semibold text-action-800 hover:bg-action-800/5"
              >
                Spara mina val
              </button>
              <button
                type="button"
                onClick={() => decide(ONLY_NECESSARY)}
                className="min-h-11 rounded-full border border-action-800 px-4 font-semibold text-action-800 hover:bg-action-800/5"
              >
                Endast nödvändiga
              </button>
              <button
                type="button"
                onClick={() => decide(ALL_GRANTED)}
                className="min-h-11 rounded-full bg-action-800 px-4 font-semibold text-white hover:bg-action-800/90"
              >
                Godkänn alla
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => decide(ONLY_NECESSARY)}
                className="min-h-11 rounded-full border border-action-800 px-4 font-semibold text-action-800 hover:bg-action-800/5"
              >
                Endast nödvändiga
              </button>
              <button
                type="button"
                onClick={() => decide(ALL_GRANTED)}
                className="min-h-11 rounded-full bg-action-800 px-4 font-semibold text-white hover:bg-action-800/90"
              >
                Godkänn alla
              </button>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className={cn("col-span-2 min-h-9 text-sm font-medium text-action-800 underline underline-offset-2")}
              >
                Anpassa
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
